import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const TermsOfServicePage = () => {
    return (
        <Box sx={{ bgcolor: '#fff', py: 6 }}>
          <Helmet>
            <title>Terms of Service — MonadX</title>
            <meta name="description" content="MonadX Terms of Service describing user rights and responsibilities." />
            <meta property="og:title" content="Terms of Service — MonadX" />
          </Helmet>
          <Container maxWidth="md">
            <Typography variant="h3" gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Effective Date: December 9, 2025
            </Typography>

            <Typography variant="body1" paragraph>
              Welcome to MonadX! These Terms of Service (“Terms”) govern your use of our website, mobile app, and services (“MonadX,” “we,” “our,” or “us”). By accessing or using MonadX, you agree to these Terms. If you do not agree, please do not use our services.
            </Typography>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>1. Eligibility</Typography>
            <Typography variant="body1" paragraph>
              MonadX is designed for family use and is suitable for children aged 4 and up. If you are under 13, you must have permission from a parent or legal guardian and use the app only under their supervision.
            </Typography>

            <Typography variant="h5" gutterBottom>2. Account Registration</Typography>
            <Typography variant="body1" paragraph>
              You may register using your email address, Google account, or Apple ID. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You agree to provide accurate and complete information during registration.
            </Typography>

            <Typography variant="h5" gutterBottom>3. Use of Services</Typography>
            <Typography variant="body1" paragraph>
              MonadX provides personalized meal planning, feedback logging, and family-friendly features. You agree to use our services only for lawful purposes and in accordance with these Terms. You may not use MonadX to:
            </Typography>
            <List>
              <ListItem><ListItemText primary="Violate any laws or regulations" /></ListItem>
              <ListItem><ListItemText primary="Upload or distribute harmful, offensive, or inappropriate content" /></ListItem>
              <ListItem><ListItemText primary="Attempt to access accounts or data that do not belong to you" /></ListItem>
            </List>

            <Typography variant="h5" gutterBottom>4. Account Deletion</Typography>
            <Typography variant="body1" paragraph>
              You may delete your account at any time through the app or by contacting us. Upon deletion, your personal data, meal history, purchase metadata, and any hashed device identifiers associated with Wallet functionality will be permanently removed from our systems.
            </Typography>

            <Typography variant="h5" gutterBottom>5. Intellectual Property</Typography>
            <Typography variant="body1" paragraph>
              All content on MonadX—including meal suggestions, graphics, logos, and software—is owned by MonadX or its licensors and protected by intellectual property laws. You may not copy, modify, or distribute any content without our written permission.
            </Typography>

            <Typography variant="h5" gutterBottom>6. Termination</Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to suspend or terminate your account if you violate these Terms or engage in harmful behavior. We may also modify or discontinue our services at any time, and we are not liable for any impact caused by such changes.
            </Typography>

            <Typography variant="h5" gutterBottom>7. Disclaimer and Limitation of Liability</Typography>
            <Typography variant="body1" paragraph>
              MonadX is provided “as is” without warranties of any kind. We do not guarantee that our services will be error-free or uninterrupted. To the maximum extent permitted by law, MonadX is not liable for any damages arising from your use of our services.
            </Typography>

            <Typography variant="h5" gutterBottom>8. Changes to These Terms</Typography>
            <Typography variant="body1" paragraph>
              We may update these Terms from time to time. If we make significant changes, we’ll notify you via email or in-app notification. Continued use of MonadX after changes means you accept the updated Terms.
            </Typography>

            <Typography variant="h5" gutterBottom>9. Contact Us</Typography>
            <Typography variant="body1" paragraph>
              If you have questions or concerns about these Terms, please reach out:
            </Typography>
            <Typography variant="body1">
              📧 <Link href="mailto:hello@monadx.me">hello@monadx.me</Link><br />
              🌐 <Link href="https://monadx.me" target="_blank" rel="noopener">monadx.me</Link>
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>10. Apple Standard EULA</Typography>
            <Typography variant="body1" paragraph>
              If you download MonadX from the Apple App Store, your use of the app is also governed by Apple’s standard End User License Agreement (EULA), which is available here:{' '}
              <Link href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener">
                Apple EULA
              </Link>
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>11. In‑App Purchases</Typography>
            <Typography variant="body1" paragraph>
              MonadX offers in‑app purchases of digital points through Apple’s In‑App Purchase system. Points are a consumable virtual currency used to generate recipes. Each recipe generation costs 5 points. Prices are displayed in the app and charged to your Apple ID; taxes may apply.
            </Typography>
            <Typography variant="body1" paragraph>
              Consumable and Non‑Restorable: Points are consumable and are generally not restorable via the App Store. If you lose access to purchased points, contact MonadX support with your purchase receipt and we will review and, at our discretion, restore points where appropriate. For fraud prevention and Wallet functionality, we may use hashed device identifiers to verify purchases and restore points.
            </Typography>
            <Typography variant="body1" paragraph>
              Refunds: Purchases of points are final and non‑refundable, except as required by law. Refunds are processed exclusively through Apple Support, unless otherwise required by applicable law. You may request a refund via Apple Support or contact MonadX at hello@monadx.me; we may issue refunds at our discretion.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>12. Governing Law & Dispute Resolution</Typography>
            <Typography variant="body1" paragraph>
              These Terms are governed by the laws of British Columbia, Canada. Any disputes arising from your use of MonadX will be resolved in the courts located in British Columbia.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>13. Entire Agreement</Typography>
            <Typography variant="body1" paragraph>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and MonadX regarding your use of our services.
            </Typography>
          </Container>
        </Box>
    )
}
export default TermsOfServicePage;